/**
 * @file User / material analysis UI — pages/Dashboard/User/MaterialAnalysisInput/components/DataSpecificitySection.tsx.
 */

import React from "react";
import { Range } from "react-range";
import Button from "../../../../../components/common/Button";
import { GeoSpec, FormValues } from "../hooks/useMaterialAnalysisForm";
import { Technology, Manufacturer } from "../hooks/useMaterialAnalysisData";
import type { DropdownOption } from "../../../../../interfaces/dropdownInterfaces";

interface DataSpecificitySectionProps {
  formValues: FormValues;
  technologies: Technology[];
  manufacturers: Manufacturer[];
  stateOptions?: DropdownOption[];
  geographicScopeOptions?: DropdownOption[];
  MIN_YEAR: number;
  MAX_YEAR: number;
  temporalSummaryLabel: string;
  geoSummaryLabel: string;
  selectedTechnologyName: string;
  onTemporalChange: (values: number[]) => void;
  onGeoChange: (next: GeoSpec) => void;
  onGeoSelectionChange: (value: string) => void;
  onTechnologyChange: (value: string) => void;
  onManufacturerChange: (value: string) => void;
  onApplyFilter?: () => void;
  onResetFilters?: () => void;
  hasData?: boolean;
  materialSelected?: boolean;
}

/** Exported const — Data Specificity Section. */
export const DataSpecificitySection: React.FC<DataSpecificitySectionProps> = ({
  formValues,
  technologies,
  manufacturers,
  stateOptions = [],
  geographicScopeOptions = [],
  MIN_YEAR,
  MAX_YEAR,
  temporalSummaryLabel,
  geoSummaryLabel,
  selectedTechnologyName,
  onTemporalChange,
  onGeoChange,
  onGeoSelectionChange,
  onTechnologyChange,
  onManufacturerChange,
  onApplyFilter,
  onResetFilters,
  hasData = false,
  materialSelected = false,
}) => {
  const { temporalRange, geoSpec, geoSelection, technologyId, manufacturerId } = formValues;

  if (!hasData) {
    return (
      <section className="analysis-card analysis-specificity-card">
        <header className="analysis-card__header">
          <h3>Data Specificity</h3>
        </header>
        <div className="specificity-placeholder">
          <p>
            {materialSelected
              ? "no data available, please relax your input conditions or select a different material"
              : "No data available. Please select a material to configure data specificity filters."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="analysis-card analysis-specificity-card">
      <header className="analysis-card__header">
        <h3>Data Specificity</h3>
      </header>

      <div className="specificity-row">
        <div className="specificity-main temporal-specificity-container">
          <div className="temporal-label-group">
            <label>Temporal Specificity</label>
            <p className="temporal-subtitle">{temporalSummaryLabel}</p>
          </div>
          <div className="temporal-wrapper">
            <div className="temporal-range-container">
              <Range
                values={temporalRange}
                step={1}
                min={MIN_YEAR}
                max={MAX_YEAR}
                onChange={onTemporalChange}
                renderTrack={({ props, children }) => {
                  const span = MAX_YEAR - MIN_YEAR || 1;
                  return (
                  <div
                    {...props}
                    className="temporal-track"
                    style={{
                      ...props.style,
                    }}
                  >
                    <div
                      className="temporal-active"
                      style={{
                        left: `${((temporalRange[0] - MIN_YEAR) / span) * 100}%`,
                        width: `${((temporalRange[1] - temporalRange[0]) / span) * 100}%`,
                      }}
                    />
                    {children}
                  </div>
                  );
                }}
                renderThumb={({ props, index, isDragged }) => (
                  <div
                    {...props}
                    className={`temporal-thumb ${isDragged ? "temporal-thumb--dragged" : ""}`}
                    style={{
                      ...props.style,
                    }}
                  >
                    <div className="temporal-thumb-value">
                      {temporalRange[index]}
                    </div>
                  </div>
                )}
              />
              <div className="temporal-range-labels">
                <span className="temporal-range-min">{MIN_YEAR}</span>
                <span className="temporal-range-max">{MAX_YEAR}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="specificity-row">
        <div className="specificity-main">
          <label>
            Geographical Specificity <span className="required">*</span>
          </label>
          <div className="geo-btn-row">
            <button
              className={geoSpec === "global" ? "geo-btn-active" : "geo-btn"}
              type="button"
              onClick={() => onGeoChange("global")}
            >
              Global
            </button>
            <button
              className={geoSpec === "india" ? "geo-btn-active" : "geo-btn"}
              type="button"
              onClick={() => onGeoChange("india")}
            >
              India
            </button>
            <button
              className={geoSpec === "state" ? "geo-btn-active" : "geo-btn"}
              type="button"
              onClick={() => onGeoChange("state")}
            >
              State Specific
            </button>
          </div>

          {geoSpec === "global" && (
            <select
              className="geo-select"
              value={geoSelection || "global"}
              onChange={(event) => onGeoSelectionChange(event.target.value)}
              disabled={!geographicScopeOptions.length}
              aria-label="Geographic Scope"
            >
              {geographicScopeOptions.map((opt) => (
                <option key={opt.id ?? opt.value} value={opt.value}>
                  {opt.label ?? opt.value}
                </option>
              ))}
            </select>
          )}

          {geoSpec === "state" && (
            <select
              className="geo-select"
              value={geoSelection}
              onChange={(event) => onGeoSelectionChange(event.target.value)}
              disabled={!stateOptions.length}
            >
              <option value="">Select</option>
              {stateOptions.map((opt) => (
                <option key={opt.id} value={opt.value}>
                  {opt.label ?? opt.value}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="specificity-row">
        <div className="specificity-main">
          <label>
            Technological Specificity <span className="required">*</span>
          </label>
          <select
            className="specificity-select"
            value={technologyId ?? ""}
            onChange={(event) => onTechnologyChange(event.target.value)}
            disabled={!technologies.length}
          >
            <option value="">Select</option>
            {technologies.map((technology) => (
              <option key={technology.id} value={technology.id}>
                {technology.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="specificity-row">
        <div className="specificity-main">
          <label>
            Manufacturer Specificity <span className="required">*</span>
          </label>
          <select
            className="specificity-select"
            value={manufacturerId ?? ""}
            onChange={(event) => onManufacturerChange(event.target.value)}
            disabled={!manufacturers.length}
          >
            <option value="">Select</option>
            {manufacturers.map((manufacturer) => (
              <option key={manufacturer.id} value={manufacturer.id}>
                {manufacturer.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {(onApplyFilter || onResetFilters) && (
        <div className="specificity-actions">
          {onResetFilters && (
            <button
              type="button"
              className="reset-filter-btn"
              onClick={onResetFilters}
            >
              Reset Filters
            </button>
          )}
          {onApplyFilter && (
            <Button
              label="Apply Filter"
              onClick={onApplyFilter}
              type="button"
              variant="default"
            />
          )}
        </div>
      )}
    </section>
  );
};

